import { supabase } from '../../lib/supabase';

/**
 * Acceso a datos de Academia Atlas — ADR-2026-08-25-la-academia-vive-en-supabase.
 *
 * Reemplaza al catálogo que vivía en `cursosCatalogo.ts` y al progreso que vivía
 * en `localStorage`. Es el ÚNICO módulo que le habla a Supabase: las pantallas
 * consumen los tipos de acá y no saben de tablas ni de snake_case.
 *
 * **El examen se corrige en el servidor.** `cargarPreguntas` trae enunciado y
 * opciones y nada más: la columna `correcta` no está concedida al rol del
 * navegador, así que ni siquiera se puede pedir. Corregir es `rendirExamen`, que
 * además hace cumplir del lado del servidor las dos reglas que antes eran
 * falsificables desde la consola: lecciones completas antes del examen, y espera
 * entre intentos contra el reloj de la base.
 */

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface SeccionResumen {
  titulo: string;
  descripcion: string;
}

export interface LeccionCurso {
  /** uuid — la clave real. */
  id: string;
  /** Lo que va en la URL. */
  slug: string;
  titulo: string;
  descripcion: string;
  duracionSeg: number;
  poster: string;
  youtubeId?: string;
  resumen: SeccionResumen[];
}

export type NivelCurso = 'Inicial' | 'Intermedio' | 'Avanzado';

export interface CursoAcademy {
  id: string;
  slug: string;
  titulo: string;
  bajada: string;
  descripcion: string;
  nivel: NivelCurso;
  portada: string;
  tono: string;
  puntajeMinimo: number;
  esperaMinutos: number;
  lecciones: LeccionCurso[];
  /** Cuántas preguntas tiene el examen. El contenido no viaja hasta rendirlo. */
  cantidadPreguntas: number;
}

/** Una pregunta como la ve el navegador: sin cuál es la correcta. */
export interface PreguntaExamen {
  id: string;
  enunciado: string;
  opciones: string[];
}

/**
 * Una pregunta ya corregida. `correcta` y `explicacion` sólo vienen si el intento
 * aprobó — lo decide el servidor, no la pantalla
 * (ADR-2026-08-25-el-intento-reprobado-no-revela-las-correctas).
 */
export interface CorreccionExamen {
  preguntaId: string;
  enunciado: string;
  opciones: string[];
  elegida: number | null;
  esCorrecta: boolean;
  correcta?: number;
  explicacion?: string;
}

export interface ResultadoExamen {
  puntaje: number;
  aciertos: number;
  total: number;
  aprobo: boolean;
  /** Si las correcciones traen la respuesta correcta. Hoy es igual a `aprobo`. */
  revela: boolean;
  rendidoAt: string;
  correcciones: CorreccionExamen[];
}

/** El progreso crudo de un curso, tal como lo guarda la base. */
export interface ProgresoFila {
  cursoId: string;
  leccionesVistas: string[];
  aprobado: boolean;
  mejorPuntaje: number | null;
  ultimoIntentoAt: string | null;
}

export interface ConfigAcademia {
  introTitulo: string;
  introDescripcion: string;
  introYoutubeId?: string;
  introPoster: string;
}

// ── Lectura ─────────────────────────────────────────────────────────────────

/**
 * Los cursos con sus lecciones, ordenados.
 *
 * Tres consultas en paralelo y el armado acá, en vez de un `select` anidado de
 * PostgREST: las lecciones y el conteo de preguntas se piden completos una sola
 * vez y se reparten por curso en memoria. Con tres cursos y catorce lecciones,
 * pedirlo anidado por curso serían N+1 viajes para ahorrar nada.
 */
export async function cargarCursos(): Promise<CursoAcademy[]> {
  const [cursos, lecciones, preguntas] = await Promise.all([
    supabase
      .from('academy_cursos')
      .select('id, slug, titulo, bajada, descripcion, nivel, portada, tono, puntaje_minimo, espera_minutos, orden')
      .order('orden'),
    supabase
      .from('academy_lecciones')
      .select('id, curso_id, slug, titulo, descripcion, duracion_seg, poster, youtube_id, resumen, orden')
      .order('orden'),
    // Sólo para contar. `correcta` ni se menciona: no está concedida.
    supabase.from('academy_preguntas').select('id, curso_id'),
  ]);

  if (cursos.error) throw cursos.error;
  if (lecciones.error) throw lecciones.error;
  if (preguntas.error) throw preguntas.error;

  const porCurso = new Map<string, LeccionCurso[]>();
  for (const l of lecciones.data ?? []) {
    const lista = porCurso.get(l.curso_id) ?? [];
    lista.push({
      id: l.id,
      slug: l.slug,
      titulo: l.titulo,
      descripcion: l.descripcion,
      duracionSeg: l.duracion_seg,
      poster: l.poster,
      youtubeId: l.youtube_id ?? undefined,
      resumen: Array.isArray(l.resumen) ? (l.resumen as SeccionResumen[]) : [],
    });
    porCurso.set(l.curso_id, lista);
  }

  const conteo = new Map<string, number>();
  for (const p of preguntas.data ?? []) {
    conteo.set(p.curso_id, (conteo.get(p.curso_id) ?? 0) + 1);
  }

  return (cursos.data ?? []).map((c) => ({
    id: c.id,
    slug: c.slug,
    titulo: c.titulo,
    bajada: c.bajada,
    descripcion: c.descripcion,
    nivel: c.nivel as NivelCurso,
    portada: c.portada,
    tono: c.tono,
    puntajeMinimo: c.puntaje_minimo,
    esperaMinutos: c.espera_minutos,
    lecciones: porCurso.get(c.id) ?? [],
    cantidadPreguntas: conteo.get(c.id) ?? 0,
  }));
}

/** El progreso del usuario logueado, por curso. RLS filtra: sólo vuelve lo suyo. */
export async function cargarProgreso(): Promise<Map<string, ProgresoFila>> {
  const { data, error } = await supabase
    .from('academy_progreso')
    .select('curso_id, lecciones_vistas, aprobado, mejor_puntaje, ultimo_intento_at');

  if (error) throw error;

  const mapa = new Map<string, ProgresoFila>();
  for (const p of data ?? []) {
    mapa.set(p.curso_id, {
      cursoId: p.curso_id,
      leccionesVistas: p.lecciones_vistas ?? [],
      aprobado: p.aprobado,
      mejorPuntaje: p.mejor_puntaje,
      ultimoIntentoAt: p.ultimo_intento_at,
    });
  }
  return mapa;
}

/** Las preguntas del examen, SIN la respuesta correcta. */
export async function cargarPreguntas(cursoId: string): Promise<PreguntaExamen[]> {
  const { data, error } = await supabase
    .from('academy_preguntas')
    .select('id, enunciado, opciones')
    .eq('curso_id', cursoId)
    .order('orden');

  if (error) throw error;

  return (data ?? []).map((p) => ({
    id: p.id,
    enunciado: p.enunciado,
    opciones: Array.isArray(p.opciones) ? (p.opciones as string[]) : [],
  }));
}

export async function cargarConfig(): Promise<ConfigAcademia | null> {
  const { data, error } = await supabase
    .from('academy_config')
    .select('intro_titulo, intro_descripcion, intro_youtube_id, intro_poster')
    .eq('id', 1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    introTitulo: data.intro_titulo,
    introDescripcion: data.intro_descripcion,
    introYoutubeId: data.intro_youtube_id ?? undefined,
    introPoster: data.intro_poster,
  };
}

// ── Escritura (todo por RPC) ────────────────────────────────────────────────

/** Marca una lección como vista. Idempotente del lado del servidor. */
export async function marcarLeccion(leccionId: string): Promise<void> {
  const { error } = await supabase.rpc('academy_marcar_leccion', {
    p_leccion_id: leccionId,
  });
  if (error) throw error;
}

/**
 * Manda el examen a corregir. El servidor valida las reglas y decide qué
 * devolver; si no se puede rendir (faltan lecciones, espera en curso), lanza.
 */
export async function rendirExamen(
  cursoId: string,
  respuestas: { preguntaId: string; elegida: number | null }[],
): Promise<ResultadoExamen> {
  const { data, error } = await supabase.rpc('academy_rendir_examen', {
    p_curso_id: cursoId,
    p_respuestas: respuestas.map((r) => ({
      pregunta_id: r.preguntaId,
      elegida: r.elegida,
    })),
  });
  if (error) throw error;
  return normalizarResultado(data);
}

/** El último intento rendido, o `null` si nunca rindió este curso. */
export async function cargarUltimoIntento(
  cursoId: string,
): Promise<ResultadoExamen | null> {
  const { data, error } = await supabase.rpc('academy_ultimo_intento', {
    p_curso_id: cursoId,
  });
  if (error) throw error;
  return data ? normalizarResultado(data) : null;
}

/** El jsonb de las funciones viene en snake_case; las pantallas no lo ven. */
function normalizarResultado(raw: any): ResultadoExamen {
  return {
    puntaje: raw.puntaje,
    aciertos: raw.aciertos,
    total: raw.total,
    aprobo: raw.aprobo,
    revela: raw.revela,
    rendidoAt: raw.rendido_at,
    correcciones: (raw.correcciones ?? []).map((c: any) => ({
      preguntaId: c.pregunta_id,
      enunciado: c.enunciado,
      opciones: c.opciones ?? [],
      elegida: c.elegida ?? null,
      esCorrecta: c.es_correcta,
      correcta: c.correcta,
      explicacion: c.explicacion,
    })),
  };
}

/**
 * Cuándo se aprobó el curso: el **primer** intento aprobado, en ms de época, o `null`
 * si todavía no lo aprobó.
 *
 * Es la fecha de finalización que va impresa en el certificado
 * (ADR-2026-08-25-la-fecha-del-certificado-sale-del-primer-intento-aprobado).
 *
 * Se deriva de `academy_intentos` y NO de `academy_progreso.ultimo_intento_at`: ese
 * campo lo pisa cada intento, y el examen se puede volver a rendir para repasar aun
 * con el curso ya aprobado. Con la fecha del último intento, quien aprobó en agosto y
 * repasó en noviembre se bajaba el mismo certificado con fecha de noviembre — un curso
 * se termina UNA vez.
 *
 * Tampoco hace falta una columna nueva: la tabla de intentos guarda el historial
 * completo, así que el dato ya está y no puede quedar desincronizado. La RLS
 * ("Cada usuario ve sus intentos") acota la consulta al usuario solo.
 */
export async function cargarAprobadoEn(cursoId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('academy_intentos')
    .select('created_at')
    .eq('curso_id', cursoId)
    .eq('aprobo', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? new Date(data.created_at).getTime() : null;
}
